import React from 'react';
import styled from '@emotion/styled';
import { css, Theme } from '@emotion/react';
import { rem } from 'polished';
import {
  colors,
  shadows,
  spaces,
  mixins,
} from '../../core/styles/theme/default';
import { H3, P2, Link, Button } from '../../primitives';
import { LinkInterface } from '~/types';

const ContactBoxWrapper = styled.section(
  ({ outerSpacing }: { outerSpacing: boolean }) => css`
    ${outerSpacing &&
    css`
      margin: ${rem(spaces.delta)} 0 0;

      ${mixins.MQ('medium')} {
        margin: ${rem(spaces.theta)} 0 0;
      }
    `}

    a + article {
      ${mixins.responsiveStyles({
        'margin-top': { zero: rem(spaces.zeta), medium: rem(spaces.theta) },
      })}
    }
  `,
);

const ContactBoxContent = styled.article(
  ({ withBurrito, theme }: { withBurrito?: boolean; theme?: Theme }) => css`
    display: flex;
    justify-content: space-between;
    ${mixins.responsiveStyles({
      'align-items': { zero: '', medium: withBurrito ? 'stretch' : 'center' },
      'flex-flow': {
        zero: 'column',
        medium: 'row',
        large: withBurrito ? 'column' : 'row',
      },
      padding: {
        zero: rem(spaces.epsilon),
        medium: withBurrito
          ? `${rem(spaces.theta)} ${rem(spaces.epsilon)}`
          : rem(spaces.zeta),
        ultra: withBurrito ? rem(spaces.eta) : rem(spaces.zeta),
      },
    })}
    background-color: ${colors.white};
    box-shadow: ${shadows.contactBox};
    border-radius: ${rem(2)};

    h3 {
      grid-area: header;
      margin-bottom: ${rem(spaces.gamma)};
    }

    footer {
      display: flex;
      flex-flow: column nowrap;
      padding-top: ${rem(spaces.gamma)};

      a,
      button {
        margin-top: ${rem(spaces.beta)};
        white-space: nowrap;
      }

      ${mixins.MQ('medium')} {
        flex-flow: row nowrap;
        align-items: center;
        padding-top: ${withBurrito ? rem(spaces.beta) : '0'};
        padding-left: ${withBurrito ? '0' : rem(spaces.gamma)};
        margin-top: -${rem(spaces.beta)};

        a,
        button {
          margin-left: ${rem(spaces.beta)};
        }
      }

      ${mixins.MQ('large')} {
        flex-flow: ${withBurrito ? 'column nowrap' : 'row nowrap'};
        align-items: stretch;
        a {
          margin-left: ${withBurrito ? 0 : rem(spaces.beta)};
        }
      }

      ${mixins.MQ('ultra')} {
        padding-top: ${withBurrito ? rem(spaces.zeta) : '0'};
      }
    }

    ${theme?.stylesExtension?.contactBox?.content}
  `,
);

const StyledLink = styled(Link)(
  ({ theme }) => css`
    .m-richtext & {
      text-decoration: none;
    }

    ${theme?.stylesExtension?.contactBox?.link}
  `,
);

const StyledButton = styled(Button)`
  .m-richtext & {
    text-decoration: none;
  }
`;

const ContactBox = ({
  withBurrito,
  outerSpacing = true,
  ...props
}: ContactBoxProps) => {
  return (
    <ContactBoxWrapper outerSpacing={outerSpacing}>
      {props.linkText ? (
        <StyledLink
          linkText={props.linkText}
          link={props.link}
          variant="internal"
        />
      ) : null}
      <ContactBoxContent withBurrito={withBurrito}>
        <header>
          <H3>{props.header}</H3>
          <P2>{props.text}</P2>
        </header>
        {props.button1?.text || props.button2?.text ? (
          <footer>
            {props.button1?.text ? (
              <StyledButton
                buttonText={props.button1.text}
                href={props.button1.href}
                aria-label={props.button1.text}
              />
            ) : null}
            {props.button2?.text ? (
              <StyledButton
                buttonText={props.button2.text}
                href={props.button2.href}
                outlined
                aria-label={props.button2.text}
              />
            ) : null}
          </footer>
        ) : null}
      </ContactBoxContent>
    </ContactBoxWrapper>
  );
};

export interface ContactBoxProps {
  withBurrito?: boolean;
  link?: LinkInterface;
  linkText?: string;
  /** ContactBox header */
  header: string;
  /** ContactBox text */
  text: React.ReactNode;
  /** ContactBox button1 */
  button1?: {
    text: string;
    href?: string;
  };
  /** ContactBox button2 */
  button2?: {
    text: string;
    href?: string;
  };
  /** Top and bottom margin */
  outerSpacing?: boolean;
}

export default ContactBox;
